import {
  AgentKeysSection,
  type GatewaySpend,
  SubscriptionsSection,
  useCredentials,
} from "@/components/ai-spend/credentials-card";
import { LineChart } from "@/components/charts/line-chart";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { LocalDate } from "@/components/ui/local-date";
import { Tabs } from "@/components/ui/tabs";
import { toast } from "@/components/ui/use-toast";
import { INPUT_CLS } from "@/lib/analysis";
import { ApiError, api } from "@/lib/api";
import { formatFixed, formatNumber } from "@/lib/i18n";
import { useApiMutation, useRowMutation } from "@/lib/use-api-mutation";
import { formatCurrency } from "@/lib/utils";
import {
  Link,
  Navigate,
  createFileRoute,
  useLoaderData,
  useNavigate,
} from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/billing/ai-spend` — admin/accountant. Spend of every LiteLLM virtual key
 * in CZK (CNB daily rate): totals, a 60-day chart, and per-key rows. A key
 * mapped to a customer can have a **closed month re-invoiced with a markup**
 * — one click creates a one-off CustomerCost line ("AI služby … 2026-06")
 * that the accountant picks into the regular monthly report. Key management
 * itself stays in the LiteLLM UI.
 *
 * E2E behaviour contract lives in `e2e/ai-spend.spec.ts`.
 */

type Rate = { rate: number; source: "cnb" | "fallback"; date: string };
type Charge = {
  id: string;
  month: string;
  spendUsd: number;
  rateCzk: number;
  rateSource: string;
  markupPct: number;
  amountCzk: number;
  customerCostId: string | null;
};
type SpendKey = {
  hash: string;
  alias: string | null;
  totalUsd: number;
  maxBudget: number | null;
  /** Why LiteLLM's own key list leaves it out (its UI sign-ins, expired, blocked). */
  hiddenReason?: "ui_session" | "expired" | "blocked" | null;
  monthUsd: number;
  prevMonthUsd: number;
  mapping: { id: string; customerId: string; customerName: string; markupPct: number } | null;
  charges: Charge[];
};
type Overview = {
  enabled: boolean;
  rate: Rate | null;
  prevMonth?: string;
  days: Array<{ day: string; spendUsd: number }>;
  keys: SpendKey[];
  /** When Hub last read the gateway's daily spend; null before the first read. */
  syncedAt?: string | null;
};
type Customer = { id: string; name: string };

type MonthTotals = {
  runs: number;
  spendUsd: number;
  meteredUsd: number;
  subscriptionUsd: number;
  projectUsd: number;
  noProjectUsd: number;
  noProjectRuns: number;
};

/** What the agents (Prokop and friends) cost on one project in a month. */
type ProjectSpend = {
  projectId: string;
  projectName: string;
  projectSlug: string;
  customerId: string | null;
  customerName: string | null;
  runs: number;
  spendUsd: number;
  meteredUsd: number;
  subscriptionUsd: number;
  /** The month as Netlion tokens: billable runs only, list price plus surcharges. */
  billable: { runs: number; listUsd: number; surchargeCzk: number; amountCzk: number };
  cap: { capCzk: number; customerTotalCzk: number; state: CapState } | null;
  charge: {
    id: string;
    amountCzk: number;
    markupPct: number;
    customerCostId: string | null;
    invoiced: boolean;
  } | null;
};

type CapState = "ok" | "warning" | "over";
type CustomerCap = {
  customerId: string;
  customerName: string;
  amountCzk: number;
  capCzk: number;
  state: CapState;
};

const DEFAULT_MARKUP = 50;

const TABS = ["clients", "agents", "work", "limits", "subscriptions", "prices"] as const;
type Tab = (typeof TABS)[number];
const searchSchema = z.object({
  tab: z.enum(TABS).optional(),
  month: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
});

/** Agent work is re-invoiced for a closed month, so that's what opens. */
function lastClosedMonth(): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 7);
}

export const Route = createFileRoute("/_app/billing/ai-spend")({
  validateSearch: searchSchema,
  component: AiSpendPage,
});

function price(n: number): string {
  return formatNumber(n, undefined, { minimumFractionDigits: 0, maximumFractionDigits: 6 });
}

function AiSpendPage() {
  const { t } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const { tab = "clients", month: monthParam } = Route.useSearch();
  const agentMonth = monthParam ?? lastClosedMonth();
  const [totals, setTotals] = useState<MonthTotals | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  const navigate = useNavigate();
  const credentials = useCredentials();
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projects, setProjects] = useState<ProjectSpend[]>([]);
  const [customerCaps, setCustomerCaps] = useState<CustomerCap[]>([]);
  // Per-key mapping drafts (customer + markup) before saving.
  const [drafts, setDrafts] = useState<Record<string, { customerId: string; markupPct: number }>>(
    {},
  );

  const allowed = user.globalRole === "admin" || user.globalRole === "accountant";

  const load = useCallback(
    () =>
      api
        .get<Overview>("/api/ai-spend")
        .then((r) => {
          setData(r);
          setError(null);
        })
        .catch((err) =>
          setError(
            err instanceof ApiError && err.status === 502
              ? t("aiSpend.page.gatewayDown")
              : t("aiSpend.page.loadFailed"),
          ),
        ),
    [t],
  );

  const loadProjects = useCallback(
    () =>
      api
        .get<{ projects: ProjectSpend[]; customers: CustomerCap[]; totals: MonthTotals }>(
          `/api/ai-spend/agent-projects?month=${agentMonth}`,
        )
        .then((r) => {
          setProjects(r.projects);
          setCustomerCaps(r.customers);
          setTotals(r.totals);
        })
        .catch(() => {}),
    [agentMonth],
  );

  useEffect(() => {
    if (!allowed) return;
    void load();
    void loadProjects();
    api
      .get<{ customers: Customer[] }>("/api/customers?pageSize=200")
      .then((r) => setCustomers(r.customers.map((c) => ({ id: c.id, name: c.name }))))
      .catch(() => {});
  }, [allowed, load, loadProjects]);

  const saveMappingMut = useRowMutation(
    (_key: SpendKey, body: Record<string, unknown>) => api.put("/api/ai-spend/mappings", body),
    {
      rowKey: (key) => key.hash,
      onSuccess: (_res, key) => {
        toast({ title: t("aiSpend.page.mappingSaved") });
        load();
        setDrafts((prev) => {
          const { [key.hash]: _, ...rest } = prev;
          return rest;
        });
      },
      onError: () => toast({ title: t("aiSpend.page.mappingFailed"), variant: "destructive" }),
    },
  );
  const chargeMut = useRowMutation(
    (key: SpendKey, month: string) =>
      api.post<{ amountCzk: number; customerName: string }>("/api/ai-spend/charges", {
        keyHash: key.hash,
        month,
      }),
    {
      rowKey: (key) => key.hash,
      onSuccess: (res) => {
        toast({
          title: t("aiSpend.page.charged", {
            amount: formatCurrency(res.amountCzk),
            customer: res.customerName,
          }),
          description: t("aiSpend.page.chargedHint"),
        });
        load();
      },
      errorMessage: (err) =>
        (err instanceof ApiError && err.detail && typeof err.detail === "object"
          ? (err.detail as { error?: string }).error
          : undefined) ?? t("aiSpend.page.chargeFailed"),
      onError: (msg) => toast({ title: msg, variant: "destructive" }),
    },
  );
  const undoMut = useApiMutation(
    (chargeId: string) => api.delete(`/api/ai-spend/charges/${encodeURIComponent(chargeId)}`),
    {
      onSuccess: () => {
        toast({ title: t("aiSpend.page.chargeRemoved") });
        load();
      },
      errorMessage: (err) =>
        (err instanceof ApiError && err.detail && typeof err.detail === "object"
          ? (err.detail as { error?: string }).error
          : undefined) ?? t("aiSpend.page.chargeRemoveFailed"),
      onError: (msg) => toast({ title: msg, variant: "destructive" }),
    },
  );

  if (!allowed) return <Navigate to="/" />;

  const rate = data?.rate ?? null;
  const czk = (usd: number) => (rate ? formatCurrency(usd * rate.rate) : "—");
  const usd = (v: number) => formatCurrency(v, "USD");

  // One "busy" key across the save + charge actions on a row — either
  // in flight disables both buttons, matching the old shared flag.
  const busyKey = saveMappingMut.runningKey ?? chargeMut.runningKey;

  function saveMapping(key: SpendKey) {
    const draft = drafts[key.hash] ?? {
      customerId: key.mapping?.customerId ?? "",
      markupPct: key.mapping?.markupPct ?? DEFAULT_MARKUP,
    };
    if (!draft.customerId) return;
    saveMappingMut.run(key, {
      keyHash: key.hash,
      keyAlias: key.alias,
      customerId: draft.customerId,
      markupPct: draft.markupPct,
    });
  }

  function chargeMonth(key: SpendKey, month: string) {
    chargeMut.run(key, month);
  }

  function undoCharge(chargeId: string) {
    undoMut.mutate(chargeId);
  }

  const agentHashes = new Set(
    (credentials.listing?.credentials ?? []).flatMap((c) => (c.gatewayHash ? [c.gatewayHash] : [])),
  );
  const nonAgentKeys = data?.keys.filter((k) => !agentHashes.has(k.hash)) ?? [];
  // Keys LiteLLM's own list leaves out stay hidden unless they cost something lately.
  const tucked = (k: SpendKey) => Boolean(k.hiddenReason) && k.monthUsd <= 0 && k.prevMonthUsd <= 0;
  const hiddenKeys = nonAgentKeys.filter(tucked);
  const clientKeys = showHidden ? nonAgentKeys : nonAgentKeys.filter((k) => !tucked(k));
  const spendByHash = new Map<string, GatewaySpend>(
    (data?.keys ?? []).map((k) => [
      k.hash,
      { monthUsd: k.monthUsd, prevMonthUsd: k.prevMonthUsd, totalUsd: k.totalUsd },
    ]),
  );
  const money = (v: number) => (rate ? `${czk(v)} (${usd(v)})` : usd(v));

  const monthTotal = data?.keys.reduce((acc, k) => acc + k.monthUsd, 0) ?? 0;
  const prevTotal = data?.keys.reduce((acc, k) => acc + k.prevMonthUsd, 0) ?? 0;
  const series =
    data?.days.map((d) => ({ day: d.day, czk: rate ? d.spendUsd * rate.rate : 0 })) ?? [];

  const prevMonth = data?.prevMonth;
  /** The unsaved (or saved) mapping draft a row's editors bind to. */
  const draftFor = (key: SpendKey) =>
    drafts[key.hash] ?? {
      customerId: key.mapping?.customerId ?? "",
      markupPct: key.mapping?.markupPct ?? DEFAULT_MARKUP,
    };

  // Columns close over the page state (drafts, customers, busyKey) — the
  // editable cells are plain `cell` renderers, the state stays up here.
  const columns: Array<DataTableColumn<SpendKey>> = [
    {
      key: "key",
      header: t("aiSpend.page.col.key"),
      cell: (key) => (
        <>
          <span className="font-medium">{key.alias ?? key.hash.slice(0, 12)}</span>
          {key.hiddenReason && (
            <Badge variant="outline" className="ml-2 font-normal" data-testid="key-hidden-reason">
              {t(`aiSpend.hidden.${key.hiddenReason}`)}
            </Badge>
          )}
          {key.maxBudget !== null && (
            <span className="ml-1 text-xs text-muted-foreground">
              {t("aiSpend.page.budget", { amount: usd(key.maxBudget) })}
            </span>
          )}
        </>
      ),
    },
    {
      key: "month",
      header: t("aiSpend.page.col.thisMonth"),
      headClassName: "text-right",
      cellClassName: "whitespace-nowrap text-right tabular-nums",
      cell: (key) => (
        <>
          <div>{czk(key.monthUsd)}</div>
          <div className="text-xs text-muted-foreground">{usd(key.monthUsd)}</div>
        </>
      ),
    },
    {
      key: "prevMonth",
      header: t("aiSpend.page.col.lastMonth"),
      headClassName: "text-right",
      cellClassName: "whitespace-nowrap text-right tabular-nums",
      cell: (key) => (
        <>
          <div>{czk(key.prevMonthUsd)}</div>
          <div className="text-xs text-muted-foreground">{usd(key.prevMonthUsd)}</div>
        </>
      ),
    },
    {
      key: "lifetime",
      header: t("aiSpend.page.col.lifetime"),
      headClassName: "text-right",
      cellClassName: "whitespace-nowrap text-right tabular-nums text-muted-foreground",
      cell: (key) => usd(key.totalUsd),
    },
    {
      key: "customer",
      header: t("aiSpend.page.col.customer"),
      cellClassName: "min-w-44",
      cell: (key) => (
        <Combobox
          options={customers.map((cust) => ({ value: cust.id, label: cust.name }))}
          value={draftFor(key).customerId}
          onChange={(v) =>
            setDrafts((prev) => ({
              ...prev,
              [key.hash]: { ...draftFor(key), customerId: v },
            }))
          }
          placeholder={t("aiSpend.page.internal")}
          aria-label={t("aiSpend.page.customerFor", { key: key.alias ?? key.hash })}
        />
      ),
    },
    {
      key: "markup",
      header: t("aiSpend.page.col.markup"),
      headClassName: "text-right",
      cellClassName: "w-24 text-right",
      cell: (key) => (
        <input
          type="number"
          min={0}
          max={500}
          value={draftFor(key).markupPct}
          onChange={(e) =>
            setDrafts((prev) => ({
              ...prev,
              [key.hash]: { ...draftFor(key), markupPct: Number(e.target.value) },
            }))
          }
          className={`${INPUT_CLS} h-8 w-20 text-right`}
          aria-label={t("aiSpend.page.markupFor", { key: key.alias ?? key.hash })}
        />
      ),
    },
    {
      key: "reinvoice",
      header: t("aiSpend.page.col.reinvoice"),
      cell: (key) => {
        const draft = draftFor(key);
        const prevCharge = key.charges.find((ch) => ch.month === prevMonth);
        const dirty =
          draft.customerId !== (key.mapping?.customerId ?? "") ||
          draft.markupPct !== (key.mapping?.markupPct ?? DEFAULT_MARKUP);
        return (
          <div className="flex flex-wrap items-center gap-2">
            {dirty && draft.customerId && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busyKey === key.hash}
                onClick={() => saveMapping(key)}
                data-testid="save-mapping"
              >
                {t("aiSpend.page.saveMapping")}
              </Button>
            )}
            {!dirty && key.mapping && prevMonth && !prevCharge && (
              <Button
                type="button"
                size="sm"
                disabled={busyKey === key.hash || key.prevMonthUsd <= 0}
                onClick={() => chargeMonth(key, prevMonth)}
                data-testid="charge-month"
              >
                {t("aiSpend.page.chargeMonth", { month: prevMonth })}
              </Button>
            )}
            {prevCharge && (
              <span className="text-xs text-muted-foreground" data-testid="charged-badge">
                ✓ {prevCharge.month}: {formatCurrency(prevCharge.amountCzk)}
                {prevCharge.customerCostId && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="ml-1 h-6 px-1 text-xs"
                    onClick={() => undoCharge(prevCharge.id)}
                    data-testid="undo-charge"
                  >
                    {t("aiSpend.page.undo")}
                  </Button>
                )}
              </span>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{t("aiSpend.page.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("aiSpend.page.subtitle")}</p>
        </div>
        {rate && (
          <Badge
            variant={rate.source === "cnb" ? "secondary" : "destructive"}
            data-testid="rate-badge"
          >
            {rate.source === "cnb" ? "ČNB" : t("aiSpend.page.rateFallback")}{" "}
            {formatNumber(rate.rate, undefined, {
              minimumFractionDigits: 3,
              maximumFractionDigits: 3,
            })}{" "}
            Kč/USD · <LocalDate value={rate.date} />
          </Badge>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {data && !data.enabled && (
        <p className="text-sm text-muted-foreground" data-testid="ai-spend-off">
          <Trans
            i18nKey="aiSpend.page.gatewayOff"
            components={{ 1: <code />, 3: <code />, 5: <code /> }}
          />
        </p>
      )}

      {data?.enabled && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                {t("aiSpend.page.monthAllKeys")}
              </CardTitle>
            </CardHeader>
            <CardContent data-testid="month-total">
              <div className="text-2xl font-bold">{czk(monthTotal)}</div>
              <div className="text-xs text-muted-foreground">{usd(monthTotal)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                {data.prevMonth
                  ? t("aiSpend.page.lastMonthWith", { month: data.prevMonth })
                  : t("aiSpend.page.col.lastMonth")}
              </CardTitle>
            </CardHeader>
            <CardContent data-testid="prev-month-total">
              <div className="text-2xl font-bold">{czk(prevTotal)}</div>
              <div className="text-xs text-muted-foreground">{usd(prevTotal)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t("aiSpend.page.dailySpend")}</CardTitle>
            </CardHeader>
            <CardContent>
              <LineChart
                data={series.map((d) => ({ label: d.day.slice(5), value: d.czk }))}
                showDots={false}
                height={80}
                formatValue={(v) => formatCurrency(v)}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {data?.enabled && (
        <p className="-mt-3 text-xs text-muted-foreground" data-testid="spend-synced-at">
          {data.syncedAt ? (
            <>
              <Trans
                i18nKey="aiSpend.page.syncedAt"
                components={{ 1: <LocalDate value={data.syncedAt} withTime /> }}
              />
            </>
          ) : (
            t("aiSpend.page.firstRead")
          )}
        </p>
      )}

      <Tabs
        value={tab}
        onValueChange={(id) =>
          void navigate({
            to: "/billing/ai-spend",
            search: { tab: id as Tab, ...(monthParam ? { month: monthParam } : {}) },
            replace: true,
          })
        }
        tabs={[
          {
            id: "clients",
            label: t("aiSpend.page.tab.clients"),
            count: data?.enabled ? nonAgentKeys.length - hiddenKeys.length : undefined,
            content: data?.enabled ? (
              <div className="space-y-3">
                <DataTable
                  columns={columns}
                  rows={clientKeys}
                  rowKey={(key) => key.hash}
                  rowTestId={() => "spend-key-row"}
                  density="compact"
                  emptyState={t("aiSpend.page.noKeys")}
                />
                {hiddenKeys.length > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowHidden((v) => !v)}
                    data-testid="toggle-hidden-keys"
                  >
                    {showHidden
                      ? t("aiSpend.page.hideKeys", { count: hiddenKeys.length })
                      : t("aiSpend.page.showKeys", {
                          count: hiddenKeys.length,
                          reasons: [
                            ...new Set(
                              hiddenKeys.map((k) =>
                                t(`aiSpend.hidden.${k.hiddenReason ?? "expired"}`),
                              ),
                            ),
                          ].join(", "),
                        })}
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">
                  <Trans
                    i18nKey="aiSpend.page.clientsNote"
                    values={{ month: data.prevMonth }}
                    components={{
                      1: (
                        // biome-ignore lint/a11y/useAnchorContent: Trans fills in the link text
                        <a
                          href="https://ai.netlion.cz/ui/"
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        />
                      ),
                    }}
                  />
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t("aiSpend.page.notConfigured")}</p>
            ),
          },
          {
            id: "agents",
            label: t("aiSpend.page.tab.agents"),
            content: <AgentKeysSection {...credentials} spendByHash={spendByHash} money={money} />,
          },
          {
            id: "work",
            label: t("aiSpend.page.tab.work"),
            content: (
              <AgentProjectsCard
                month={agentMonth}
                onMonth={(m) =>
                  void navigate({
                    to: "/billing/ai-spend",
                    search: { tab: "work", month: m },
                    replace: true,
                  })
                }
                projects={projects}
                customerCaps={customerCaps}
                totals={totals}
                onChanged={loadProjects}
              />
            ),
          },
          { id: "limits", label: t("aiSpend.page.tab.limits"), content: <LimitUsageCard /> },
          {
            id: "subscriptions",
            label: t("aiSpend.page.tab.subscriptions"),
            count: credentials.listing?.credentials.filter((c) => c.kind !== "gateway_key").length,
            content: <SubscriptionsSection {...credentials} />,
          },
          { id: "prices", label: t("aiSpend.page.tab.prices"), content: <ModelPricesCard /> },
        ]}
      />
    </div>
  );
}

/**
 * Agent work per project in the month and its one-click charge as Netlion
 * tokens; customers near or over their monthly cap are flagged, never blocked.
 */
function AgentProjectsCard({
  month,
  onMonth,
  projects,
  customerCaps,
  totals,
  onChanged,
}: {
  month: string;
  onMonth: (month: string) => void;
  projects: ProjectSpend[];
  customerCaps: CustomerCap[];
  totals: MonthTotals | null;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const chargeMut = useRowMutation(
    (row: ProjectSpend) =>
      api.post("/api/ai-spend/agent-charges", { projectId: row.projectId, month }),
    {
      rowKey: (row) => row.projectId,
      onSuccess: () => {
        toast({ title: t("aiSpend.work.charged"), variant: "success" });
        onChanged();
      },
      onError: (msg) =>
        toast({ title: t("aiSpend.work.chargeFailed"), description: msg, variant: "destructive" }),
    },
  );
  const undoMut = useApiMutation((id: string) => api.delete(`/api/ai-spend/agent-charges/${id}`), {
    onSuccess: () => {
      toast({ title: t("aiSpend.work.withdrawn"), variant: "success" });
      onChanged();
    },
    fallbackMessage: t("aiSpend.work.tryAgain"),
    onError: (msg) =>
      toast({ title: t("aiSpend.work.undoFailed"), description: msg, variant: "destructive" }),
  });

  const columns: Array<DataTableColumn<ProjectSpend>> = [
    { key: "project", header: t("aiSpend.work.col.project"), cell: (row) => row.projectName },
    {
      key: "customer",
      header: t("aiSpend.page.col.customer"),
      cell: (row) => row.customerName ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: "runs",
      header: t("aiSpend.work.col.runs"),
      headClassName: "text-right",
      cellClassName: "text-right",
      cell: (row) => row.runs,
    },
    {
      key: "metered",
      header: t("aiSpend.work.col.gateway"),
      headClassName: "text-right",
      cellClassName: "text-right tabular-nums",
      cell: (row) => `$${formatFixed(row.meteredUsd, 2)}`,
    },
    {
      key: "subscription",
      header: t("aiSpend.work.col.subscription"),
      headClassName: "text-right",
      cellClassName: "text-right tabular-nums text-muted-foreground",
      cell: (row) => `$${formatFixed(row.subscriptionUsd, 2)}`,
    },
    {
      key: "cost",
      header: t("aiSpend.work.col.total"),
      headClassName: "text-right",
      cellClassName: "text-right tabular-nums font-medium",
      cell: (row) => `$${formatFixed(row.spendUsd, 2)}`,
    },
    {
      key: "tokens",
      header: t("aiSpend.work.col.tokens"),
      headClassName: "text-right",
      cellClassName: "text-right tabular-nums",
      cell: (row) => (
        <div className="flex items-center justify-end gap-2">
          {row.cap && row.cap.state !== "ok" && <CapBadge state={row.cap.state} />}
          <span
            title={t("aiSpend.work.tokensHint", {
              runs: row.billable.runs,
              list: `$${formatFixed(row.billable.listUsd, 2)}`,
              surcharge: formatCurrency(row.billable.surchargeCzk),
            })}
          >
            {formatCurrency(row.billable.amountCzk)}
          </span>
        </div>
      ),
    },
    {
      key: "charge",
      header: t("aiSpend.work.col.reinvoice", { month }),
      headClassName: "text-right",
      cellClassName: "text-right",
      cell: (row) => {
        if (row.charge) {
          return (
            <div className="flex items-center justify-end gap-2">
              <Badge variant="secondary">{formatCurrency(row.charge.amountCzk)}</Badge>
              {row.charge.invoiced ? (
                <span className="text-xs text-muted-foreground">{t("aiSpend.work.invoiced")}</span>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={undoMut.pending}
                  onClick={() => undoMut.mutate(row.charge?.id ?? "")}
                >
                  {t("aiSpend.work.undo")}
                </Button>
              )}
            </div>
          );
        }
        return (
          <Button
            size="sm"
            variant="outline"
            disabled={!row.customerId || chargeMut.runningKey !== null}
            onClick={() => chargeMut.run(row)}
          >
            {row.customerId ? t("aiSpend.work.charge") : t("aiSpend.work.noCustomer")}
          </Button>
        );
      },
    },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4 space-y-0">
        <CardTitle>{t("aiSpend.work.title")}</CardTitle>
        <div className="w-44">
          <input
            type="month"
            value={month}
            onChange={(e) => e.target.value && onMonth(e.target.value)}
            className={`${INPUT_CLS} h-8`}
            aria-label={t("aiSpend.work.month")}
            data-testid="agent-work-month"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {totals && <MonthTotalsStrip totals={totals} />}
        <p className="text-sm text-muted-foreground">{t("aiSpend.work.intro", { month })}</p>
        <CustomerCapsList caps={customerCaps} />
        <DataTable
          columns={columns}
          rows={projects}
          rowKey={(row) => row.projectId}
          rowTestId={() => "agent-project-row"}
          density="compact"
          emptyState={t("aiSpend.work.empty")}
        />
      </CardContent>
    </Card>
  );
}

function CapBadge({ state }: { state: "warning" | "over" }) {
  const { t } = useTranslation();
  return (
    <Badge variant={state === "over" ? "destructive" : "warning"} data-testid={`cap-${state}`}>
      {t(state === "over" ? "aiSpend.work.cap.over" : "aiSpend.work.cap.warning")}
    </Badge>
  );
}

/** Customers whose month reached 80 % of the cap: past it the client must consent. */
function CustomerCapsList({ caps }: { caps: CustomerCap[] }) {
  const { t } = useTranslation();
  const flagged = caps.filter(
    (c): c is CustomerCap & { state: "warning" | "over" } => c.state !== "ok",
  );
  if (flagged.length === 0) return null;
  return (
    <div className="space-y-2 rounded-lg border bg-muted/30 p-3 text-sm" data-testid="cap-list">
      <p className="font-medium">{t("aiSpend.work.cap.title")}</p>
      <ul className="space-y-1">
        {flagged.map((c) => (
          <li key={c.customerId} className="flex flex-wrap items-center gap-2">
            <CapBadge state={c.state} />
            <span>
              {t("aiSpend.work.cap.line", {
                customer: c.customerName,
                amount: formatCurrency(c.amountCzk),
                cap: formatCurrency(c.capCzk),
              })}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">{t("aiSpend.work.cap.hint")}</p>
    </div>
  );
}

/** The month's runs split two ways, each adding up to the total. */
function MonthTotalsStrip({ totals }: { totals: MonthTotals }) {
  const { t } = useTranslation();
  const usd = (n: number) => `$${formatFixed(n, 2)}`;
  const cell = (label: string, value: string, hint?: string) => (
    <div className="space-y-0.5">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-semibold tabular-nums">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
  return (
    <div
      className="grid gap-4 rounded-lg border bg-muted/30 p-3 text-sm sm:grid-cols-[auto_auto_1fr_auto_1fr] sm:items-center"
      data-testid="agent-work-totals"
    >
      {cell(
        t("aiSpend.totals.allRuns"),
        usd(totals.spendUsd),
        t("aiSpend.totals.runs", { count: totals.runs }),
      )}
      <div className="hidden text-muted-foreground sm:block">=</div>
      <div className="grid grid-cols-2 gap-3">
        {cell(t("aiSpend.totals.gateway"), usd(totals.meteredUsd), t("aiSpend.totals.perToken"))}
        {cell(
          t("aiSpend.totals.subscription"),
          usd(totals.subscriptionUsd),
          t("aiSpend.totals.estimate"),
        )}
      </div>
      <div className="hidden text-muted-foreground sm:block">=</div>
      <div className="grid grid-cols-2 gap-3">
        {cell(
          t("aiSpend.totals.onProjects"),
          usd(totals.projectUsd),
          t("aiSpend.totals.canReinvoice"),
        )}
        {cell(
          t("aiSpend.totals.noProject"),
          usd(totals.noProjectUsd),
          t("aiSpend.totals.noProjectRuns", { count: totals.noProjectRuns }),
        )}
      </div>
    </div>
  );
}

type LimitRow = {
  agentSlug: string;
  agentName: string;
  model: string | null;
  runs: number;
  usd: number;
  wastedUsd: number;
  wastedRuns: number;
};
type LimitPool = {
  pool: "subscription" | "codex";
  subscription: { id: string; name: string; plan: string | null } | null;
  runs: number;
  usd: number;
  rows: LimitRow[];
};
type LimitTicket = {
  workItemId: string;
  globalId: number;
  title: string;
  runs: number;
  agents: number;
  usd: number;
};
type LimitUsage = { from: string; to: string; pools: LimitPool[]; tickets: LimitTicket[] };

const LIMIT_PERIODS = [7, 30, 90] as const;

/** Which agents and models use up the flat plans, so it's clear where to save. */
function LimitUsageCard() {
  const { t } = useTranslation();
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<LimitUsage | null>(null);

  useEffect(() => {
    const to = new Date();
    const from = new Date(to.getTime() - (days - 1) * 86_400_000);
    const day = (d: Date) => d.toISOString().slice(0, 10);
    api
      .get<LimitUsage>(`/api/ai-spend/usage?from=${day(from)}&to=${day(to)}`)
      .then(setData)
      .catch(() => {});
  }, [days]);

  const usd = (n: number) => `$${formatFixed(n, 2)}`;
  const poolColumns = (pool: LimitPool): Array<DataTableColumn<LimitRow>> => [
    { key: "agent", header: t("aiSpend.limits.col.agent"), cell: (r) => r.agentName },
    {
      key: "model",
      header: t("aiSpend.limits.col.model"),
      cell: (r) => r.model ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: "runs",
      header: t("aiSpend.limits.col.runs"),
      headClassName: "text-right",
      cellClassName: "text-right tabular-nums",
      cell: (r) => r.runs,
    },
    {
      key: "share",
      header: t("aiSpend.limits.col.share"),
      headClassName: "text-right",
      cellClassName: "text-right tabular-nums font-medium",
      cell: (r) => `${formatFixed(pool.usd > 0 ? (100 * r.usd) / pool.usd : 0, 1)} %`,
    },
    {
      key: "cost",
      header: t("aiSpend.limits.col.cost"),
      headClassName: "text-right",
      cellClassName: "text-right tabular-nums text-muted-foreground",
      cell: (r) => usd(r.usd),
    },
    {
      key: "wasted",
      header: t("aiSpend.limits.col.wasted"),
      headClassName: "text-right",
      cellClassName: "text-right tabular-nums",
      cell: (r) =>
        r.wastedRuns > 0 ? (
          t("aiSpend.limits.wasted", { amount: usd(r.wastedUsd), count: r.wastedRuns })
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ];
  const ticketColumns: Array<DataTableColumn<LimitTicket>> = [
    {
      key: "ticket",
      header: t("aiSpend.limits.col.ticket"),
      cell: (r) => (
        <Link to="/work-item/$id" params={{ id: String(r.globalId) }} className="hover:underline">
          <span className="font-medium">#{r.globalId}</span> {r.title}
        </Link>
      ),
    },
    {
      key: "runs",
      header: t("aiSpend.limits.col.runs"),
      headClassName: "text-right",
      cellClassName: "text-right tabular-nums",
      cell: (r) => r.runs,
    },
    {
      key: "agents",
      header: t("aiSpend.limits.col.agents"),
      headClassName: "text-right",
      cellClassName: "text-right tabular-nums",
      cell: (r) => r.agents,
    },
    {
      key: "cost",
      header: t("aiSpend.limits.col.cost"),
      headClassName: "text-right",
      cellClassName: "text-right tabular-nums",
      cell: (r) => usd(r.usd),
    },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4 space-y-0">
        <CardTitle>{t("aiSpend.limits.title")}</CardTitle>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className={`${INPUT_CLS} h-8 w-44`}
          aria-label={t("aiSpend.limits.period")}
          data-testid="limit-usage-period"
        >
          {LIMIT_PERIODS.map((d) => (
            <option key={d} value={d}>
              {t("aiSpend.limits.days", { count: d })}
            </option>
          ))}
        </select>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground">{t("aiSpend.limits.intro")}</p>
        {data && data.pools.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="limit-usage-empty">
            {t("aiSpend.limits.empty")}
          </p>
        )}
        {data?.pools.map((pool) => (
          <section
            key={`${pool.pool}:${pool.subscription?.id ?? ""}`}
            className="space-y-2"
            data-testid="limit-usage-pool"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="font-semibold">
                {pool.subscription
                  ? pool.subscription.name
                  : t(`aiSpend.limits.unrecorded.${pool.pool}`)}
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {t(`aiSpend.limits.pool.${pool.pool}`)}
                  {pool.subscription?.plan ? ` · ${pool.subscription.plan}` : ""}
                </span>
              </h3>
              <span className="text-sm text-muted-foreground tabular-nums">
                {t("aiSpend.limits.poolTotal", { amount: usd(pool.usd), count: pool.runs })}
              </span>
            </div>
            <DataTable
              columns={poolColumns(pool)}
              rows={pool.rows}
              rowKey={(r) => `${r.agentSlug}:${r.model ?? ""}`}
              rowTestId={() => "limit-usage-row"}
              density="compact"
            />
          </section>
        ))}
        {data && data.tickets.length > 0 && (
          <section className="space-y-2">
            <h3 className="font-semibold">{t("aiSpend.limits.tickets")}</h3>
            <p className="text-sm text-muted-foreground">{t("aiSpend.limits.ticketsIntro")}</p>
            <DataTable
              columns={ticketColumns}
              rows={data.tickets}
              rowKey={(r) => r.workItemId}
              rowTestId={() => "limit-usage-ticket"}
              density="compact"
            />
          </section>
        )}
      </CardContent>
    </Card>
  );
}

type ModelPrice = {
  model: string;
  inputPerMTok: number;
  outputPerMTok: number;
  cacheWritePerMTok: number;
  cacheReadPerMTok: number;
  source: string;
  updatedAt: string;
};
type ModelPrices = {
  gatewayConfigured: boolean;
  cache: { models: number; loadedAt: string | null };
  models: ModelPrice[];
};

/**
 * What each model costs per million tokens — the prices every agent-run cost
 * on this page is computed from. They come from the gateway's own catalogue
 * and refresh daily, so the ledger follows a provider reprice on its own;
 * "Refresh now" is for when you don't want to wait for the nightly job.
 */
function ModelPricesCard() {
  const { t } = useTranslation();
  const [data, setData] = useState<ModelPrices | null>(null);

  const load = useCallback(
    () =>
      api
        .get<ModelPrices>("/api/ai-spend/model-prices")
        .then(setData)
        .catch(() => {}),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useApiMutation(
    () => api.post<{ updated: number }>("/api/ai-spend/model-prices/refresh", {}),
    {
      onSuccess: (res) => {
        toast({ title: t("aiSpend.prices.refreshed", { count: res.updated }) });
        void load();
      },
      fallbackMessage: t("aiSpend.prices.refreshFailed"),
      onError: (msg) => toast({ title: msg, variant: "destructive" }),
    },
  );

  const refreshedAt = data?.models.reduce<string | null>(
    (latest, m) => (latest === null || m.updatedAt > latest ? m.updatedAt : latest),
    null,
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle>{t("aiSpend.prices.title")}</CardTitle>
        <Button
          size="sm"
          variant="outline"
          disabled={refresh.pending}
          onClick={() => void refresh.mutate()}
          data-testid="refresh-model-prices"
        >
          {refresh.pending ? t("aiSpend.prices.refreshing") : t("aiSpend.prices.refreshNow")}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{t("aiSpend.prices.intro")}</p>
        {data && data.models.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="model-prices-empty">
            {data.gatewayConfigured
              ? t("aiSpend.prices.noneLoaded")
              : t("aiSpend.prices.gatewayOff")}
          </p>
        ) : (
          <>
            <DataTable
              columns={[
                {
                  key: "model",
                  header: t("aiSpend.prices.col.model"),
                  cell: (r: ModelPrice) => r.model,
                },
                {
                  key: "in",
                  header: t("aiSpend.prices.col.input"),
                  headClassName: "text-right",
                  cellClassName: "text-right",
                  cell: (r: ModelPrice) => `$${price(r.inputPerMTok)}`,
                },
                {
                  key: "out",
                  header: t("aiSpend.prices.col.output"),
                  headClassName: "text-right",
                  cellClassName: "text-right",
                  cell: (r: ModelPrice) => `$${price(r.outputPerMTok)}`,
                },
                {
                  key: "cw",
                  header: t("aiSpend.prices.col.cacheWrite"),
                  headClassName: "text-right",
                  cellClassName: "text-right",
                  cell: (r: ModelPrice) => `$${price(r.cacheWritePerMTok)}`,
                },
                {
                  key: "cr",
                  header: t("aiSpend.prices.col.cacheRead"),
                  headClassName: "text-right",
                  cellClassName: "text-right",
                  cell: (r: ModelPrice) => `$${price(r.cacheReadPerMTok)}`,
                },
              ]}
              rows={data?.models ?? []}
              rowKey={(r) => r.model}
              rowTestId={() => "model-price-row"}
              density="compact"
              emptyState={t("aiSpend.prices.empty")}
            />
            {refreshedAt && (
              <p className="text-xs text-muted-foreground" data-testid="model-prices-updated">
                <Trans
                  i18nKey="aiSpend.prices.lastRefreshed"
                  components={{ 1: <LocalDate value={refreshedAt} withTime /> }}
                />
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
